// Sign-in usecase — pure orchestration. Validates input + calls the port
// + persists the session via the storage port. No React, no fetch.

import type { AuthPort, AuthStoragePort } from '../ports'
import { AuthError, type AuthSession, type Credentials } from '../entity'

export interface SignInDeps {
  auth: AuthPort
  storage: AuthStoragePort
}

/** Trim email + reject empty fields, then delegate to the AuthPort. On
 *  success, persist the session. The store layer subscribes to the
 *  storage port indirectly via the hook's `initialize()` on mount —
 *  this usecase only writes; reads happen at the boundary. */
export async function signIn(
  deps: SignInDeps,
  credentials: Credentials,
): Promise<AuthSession> {
  const email = credentials.email.trim()
  const password = credentials.password
  if (!email || !password) {
    throw new AuthError('invalid_credentials', 'Email et mot de passe requis')
  }
  const session = await deps.auth.login({ email, password })
  deps.storage.write(session)
  return session
}

export interface SignOutDeps {
  auth: AuthPort
  storage: AuthStoragePort
}

/** Best-effort server-side logout + local storage purge. Even if the BE
 *  call fails (offline / expired token), the local session is cleared
 *  so the LoginScreen surfaces on next render — invariant W5 reset must
 *  not be blocked by a network failure. */
export async function signOut(deps: SignOutDeps): Promise<void> {
  try {
    await deps.auth.logout()
  } catch {
    // Server-side cookie may already be gone — local purge below is
    // the source of truth for "this client is signed out".
  }
  deps.storage.clear()
}
